import { useState, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencil } from '@fortawesome/free-solid-svg-icons'
import { useHideReadings, useUnhideReadings } from '../hooks/useDataManagement'
import { cn } from '../shared/utils/cn'
import { getLocalSettings, onSettingsChange } from '../shared/utils/localStorage'
import { Switch } from './Switch'

const FIVE_MINUTES = 5 * 60 * 1000 // 300,000 ms

function getEditKey(sensorPort: string | string[]): string {
  return Array.isArray(sensorPort) ? sensorPort.join('.') : sensorPort
}

function parseEditParam(param: string | null): Set<string> {
  if (!param) return new Set()
  return new Set(param.split(',').filter(Boolean))
}

function adjust(ts: number, tsType: 'start' | 'end'): number {
  // Snap UTC timestamp directly to 5-minute boundaries
  // No timezone conversion needed - all data is UTC
  const adjustedTime =
    tsType === 'start'
      ? Math.floor(ts / FIVE_MINUTES) * FIVE_MINUTES
      : Math.ceil(ts / FIVE_MINUTES) * FIVE_MINUTES
  return adjustedTime
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleString()
}

export interface EditModeProps {
  selectXRange: number[]
  addTimestamp: (timestamp: number) => void
  zoomEnabled: boolean
  showHiddenData: boolean
}

interface ChartEditWrapperProps {
  sigfoxId: string
  sensorPort: string | string[]
  sensorName: string
  hasHiddenData?: boolean
  editDisabled?: boolean
  editDisabledReason?: string
  children: (editModeProps: EditModeProps) => React.ReactNode
}

export function ChartEditWrapper({
  sigfoxId,
  sensorPort,
  sensorName,
  hasHiddenData = false,
  editDisabled = false,
  editDisabledReason,
  children,
}: ChartEditWrapperProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [startTimestamp, setStartTimestamp] = useState(0)
  const [endTimestamp, setEndTimestamp] = useState(0)
  const [chartEditEnabled, setChartEditEnabled] = useState(() => getLocalSettings().chartEditEnabled)

  const editKey = getEditKey(sensorPort)
  const editSensors = parseEditParam(searchParams.get('edit'))
  const isEditMode = editSensors.has(editKey)

  const showHiddenSensors = parseEditParam(searchParams.get('showHidden'))
  const showHiddenData = showHiddenSensors.has(editKey)

  const updateSearchParams = useCallback((updater: (params: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams)
    updater(next)
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const setShowHiddenData = useCallback((show: boolean) => {
    updateSearchParams((params) => {
      const sensors = parseEditParam(params.get('showHidden'))
      if (show) {
        sensors.add(editKey)
      } else {
        sensors.delete(editKey)
      }
      if (sensors.size > 0) {
        params.set('showHidden', Array.from(sensors).join(','))
      } else {
        params.delete('showHidden')
      }
    })
  }, [editKey, updateSearchParams])

  useEffect(() => {
    return onSettingsChange((settings) => {
      setChartEditEnabled(settings.chartEditEnabled)
    })
  }, [])

  const hideReadingsMutation = useHideReadings()
  const unhideReadingsMutation = useUnhideReadings()

  const resetTimestamps = useCallback(() => {
    setStartTimestamp(0)
    setEndTimestamp(0)
  }, [])

  const addTimestamp = useCallback(
    (timestamp: number) => {
      if (!isEditMode) return

      // Third click: reset and start over
      if (startTimestamp !== 0 && endTimestamp !== 0) {
        setStartTimestamp(adjust(timestamp, 'start'))
        setEndTimestamp(0)
        return
      }

      // First click: set start timestamp
      if (startTimestamp === 0) {
        setStartTimestamp(adjust(timestamp, 'start'))
        return
      }

      // Second click: set end timestamp
      let newEnd = adjust(timestamp, 'end')
      let newStart = startTimestamp

      // If second < first, swap them
      if (newEnd < newStart) {
        const temp = newStart
        newStart = adjust(timestamp, 'start')
        newEnd = adjust(temp, 'end')
        setStartTimestamp(newStart)
      }

      setEndTimestamp(newEnd)
    },
    [isEditMode, startTimestamp, endTimestamp]
  )

  const handleHideThisSensor = useCallback(() => {
    if (startTimestamp === 0 || endTimestamp === 0) return

    const currentSensorPort = Array.isArray(sensorPort) ? sensorPort[0] : sensorPort

    // Convert milliseconds to seconds for the API
    const fromSeconds = Math.floor(startTimestamp / 1000)
    const toSeconds = Math.floor(endTimestamp / 1000)

    hideReadingsMutation.mutate(
      { sigfoxId, from: fromSeconds, to: toSeconds, sensorPort: currentSensorPort },
      {
        onSuccess: () => {
          resetTimestamps()
        },
      }
    )
  }, [sigfoxId, sensorPort, startTimestamp, endTimestamp, hideReadingsMutation, resetTimestamps])

  const handleHideAllSensors = useCallback(() => {
    if (startTimestamp === 0 || endTimestamp === 0) return

    // Convert milliseconds to seconds for the API
    const fromSeconds = Math.floor(startTimestamp / 1000)
    const toSeconds = Math.floor(endTimestamp / 1000)

    hideReadingsMutation.mutate(
      { sigfoxId, from: fromSeconds, to: toSeconds },
      {
        onSuccess: () => {
          resetTimestamps()
        },
      }
    )
  }, [sigfoxId, startTimestamp, endTimestamp, hideReadingsMutation, resetTimestamps])

  const handleRestoreThisSensor = useCallback(() => {
    if (startTimestamp === 0 || endTimestamp === 0) return

    const currentSensorPort = Array.isArray(sensorPort) ? sensorPort[0] : sensorPort

    // Convert milliseconds to seconds for the API
    const fromSeconds = Math.floor(startTimestamp / 1000)
    const toSeconds = Math.floor(endTimestamp / 1000)

    unhideReadingsMutation.mutate(
      { sigfoxId, from: fromSeconds, to: toSeconds, sensorPort: currentSensorPort },
      {
        onSuccess: () => {
          resetTimestamps()
        },
      }
    )
  }, [sigfoxId, sensorPort, startTimestamp, endTimestamp, unhideReadingsMutation, resetTimestamps])

  const handleRestoreAllSensors = useCallback(() => {
    if (startTimestamp === 0 || endTimestamp === 0) return

    // Convert milliseconds to seconds for the API
    const fromSeconds = Math.floor(startTimestamp / 1000)
    const toSeconds = Math.floor(endTimestamp / 1000)

    unhideReadingsMutation.mutate(
      { sigfoxId, from: fromSeconds, to: toSeconds },
      {
        onSuccess: () => {
          resetTimestamps()
        },
      }
    )
  }, [sigfoxId, startTimestamp, endTimestamp, unhideReadingsMutation, resetTimestamps])

  const toggleEditMode = useCallback(() => {
    if (isEditMode) {
      resetTimestamps()
    }
    // Combine edit + showHidden updates in a single searchParams update
    updateSearchParams((params) => {
      // Toggle edit mode
      const editSensors = parseEditParam(params.get('edit'))
      if (isEditMode) {
        editSensors.delete(editKey)
      } else {
        editSensors.add(editKey)
      }
      if (editSensors.size > 0) {
        params.set('edit', Array.from(editSensors).join(','))
      } else {
        params.delete('edit')
      }

      // Auto-toggle showHidden
      const hiddenSensors = parseEditParam(params.get('showHidden'))
      if (isEditMode) {
        // Exiting edit mode - turn off
        hiddenSensors.delete(editKey)
      } else if (hasHiddenData) {
        // Entering edit mode - turn on if hidden data exists
        hiddenSensors.add(editKey)
      }
      if (hiddenSensors.size > 0) {
        params.set('showHidden', Array.from(hiddenSensors).join(','))
      } else {
        params.delete('showHidden')
      }
    })
  }, [isEditMode, hasHiddenData, editKey, resetTimestamps, updateSearchParams])

  const selectXRange: number[] = [startTimestamp, endTimestamp].filter((ts) => ts !== 0)

  const editModeProps: EditModeProps = {
    selectXRange,
    addTimestamp,
    zoomEnabled: !isEditMode,
    showHiddenData: isEditMode && showHiddenData,
  }

  const getStatusText = () => {
    if (startTimestamp === 0) {
      return 'Click on chart to select start of range'
    }
    if (endTimestamp === 0) {
      return `Start: ${formatTimestamp(startTimestamp)} - Click to select end of range`
    }
    return `Selected: ${formatTimestamp(startTimestamp)} to ${formatTimestamp(endTimestamp)}`
  }

  const hasRangeSelected = startTimestamp !== 0 && endTimestamp !== 0
  const isLoading = hideReadingsMutation.isPending || unhideReadingsMutation.isPending

  return (
    <div className="relative">
      {/* Edit button + Show Hidden toggle - top right corner */}
      {chartEditEnabled && (
        <div className="absolute top-0 right-2 z-10 h-[46px] flex items-center gap-2">
          {isEditMode && hasHiddenData && (
            <Switch label="Show Hidden" state={showHiddenData} onChange={() => setShowHiddenData(!showHiddenData)} labelClassName="text-white" />
          )}
          <button
            onClick={editDisabled ? undefined : toggleEditMode}
            className={cn(
              'px-3 py-1 -mt-2 rounded text-sm font-medium transition-colors',
              editDisabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : isEditMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            )}
            disabled={isLoading || editDisabled}
            title={editDisabled ? editDisabledReason : undefined}
          >
            <FontAwesomeIcon icon={faPencil} className="mr-1.5" />
            {isEditMode ? 'Done' : 'Edit'}
          </button>
        </div>
      )}

      {/* Chart content */}
      {children(editModeProps)}

      {/* Edit mode control panel */}
      {isEditMode && (
        <div className="mt-2 p-3 bg-gray-100 rounded border border-gray-300">
          {/* Status text */}
          <p className="text-sm text-gray-600 mb-3 text-center">{getStatusText()}</p>

          {/* Action buttons + Show Hidden Data toggle */}
          <div className="flex gap-2 flex-wrap justify-center items-center">
            <button
              onClick={handleHideThisSensor}
              disabled={!hasRangeSelected || isLoading}
              className={cn(
                'px-4 py-2 rounded font-medium text-sm transition-colors',
                hasRangeSelected && !isLoading
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-red-300 text-red-100 cursor-not-allowed'
              )}
            >
              {hideReadingsMutation.isPending ? 'Hiding...' : 'Hide This Sensor'}
            </button>

            <button
              onClick={handleHideAllSensors}
              disabled={!hasRangeSelected || isLoading}
              className={cn(
                'px-4 py-2 rounded font-medium text-sm transition-colors',
                hasRangeSelected && !isLoading
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-red-300 text-red-100 cursor-not-allowed'
              )}
            >
              {hideReadingsMutation.isPending ? 'Hiding...' : 'Hide All Sensors'}
            </button>

            <button
              onClick={handleRestoreThisSensor}
              disabled={!hasRangeSelected || isLoading}
              className={cn(
                'px-4 py-2 rounded font-medium text-sm transition-colors',
                hasRangeSelected && !isLoading
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-green-300 text-green-100 cursor-not-allowed'
              )}
            >
              {unhideReadingsMutation.isPending ? 'Restoring...' : 'Restore This Sensor'}
            </button>

            <button
              onClick={handleRestoreAllSensors}
              disabled={!hasRangeSelected || isLoading}
              className={cn(
                'px-4 py-2 rounded font-medium text-sm transition-colors',
                hasRangeSelected && !isLoading
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-green-300 text-green-100 cursor-not-allowed'
              )}
            >
              {unhideReadingsMutation.isPending ? 'Restoring...' : 'Restore All Sensors'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
